import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useChatBadge } from '../context/ChatBadgeContext';
import { useBadge } from '../context/BadgeContext';
import authApi, { UserProfile, SubscriptionResponse, CommunityUserStatsResponse } from '../services/authApi';
import './Sidebar.css';

const navGroups = [
  {
    label: 'LEARN',
    items: [
      { path: '/courses',   icon: <BookIcon />,    label: 'Learning'    },
      { path: '/screeners', icon: <ScreenerIcon />, label: 'Screeners'  },
    ],
  },
  {
    label: 'COMMUNITY',
    items: [
      { path: '/chat-support',  icon: <SupportIcon />,     label: 'Chat Support'  },
      { path: '/leaderboard',   icon: <LeaderboardIcon />, label: 'Leaderboard'   },
      { path: '/announcements', icon: <BellIcon />,        label: 'Updates'       },
    ],
  },
];

function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: { collapsed: boolean; onToggle: () => void; mobileOpen?: boolean; onMobileClose?: () => void }) {
  const { user, isAuthenticated } = useAuth();
  const { unread } = useChatBadge();
  const { counts } = useBadge();
  const navigate = useNavigate();

  const isAdmin = user?.roles?.some((r: string) => r.toUpperCase().includes('ADMIN'));

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''}`}>
      {/* Collapse toggle */}
      <button className="sidebar-toggle" onClick={onToggle} aria-label="Toggle sidebar">
        <CollapseIcon collapsed={collapsed} />
      </button>

      <nav className="sidebar-nav">
        {navGroups.map((group) => (
          <div key={group.label} className="sidebar-group">
            {!collapsed && <span className="sidebar-group-label">{group.label}</span>}
            <ul className="sidebar-list">
              {group.items.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `sidebar-item ${isActive ? 'sidebar-item--active' : ''}`
                    }
                    title={collapsed ? item.label : undefined}
                    onClick={onMobileClose}
                  >
                    <span className="sidebar-item-icon">{item.icon}</span>
                    {!collapsed && <span className="sidebar-item-label">{item.label}</span>}
                    {item.path === '/chat-support' && unread > 0 && (
                      <span className="sidebar-badge">{unread > 9 ? '9+' : unread}</span>
                    )}
                    {item.path === '/announcements' && counts.UPDATES > 0 && (
                      <span className="sidebar-badge">{counts.UPDATES > 9 ? '9+' : counts.UPDATES}</span>
                    )}
                    {item.path === '/screeners' && counts.SCREENERS > 0 && (
                      <span className="sidebar-badge">{counts.SCREENERS > 9 ? '9+' : counts.SCREENERS}</span>
                    )}
                    {item.path === '/courses' && counts.LEARNING > 0 && (
                      <span className="sidebar-badge">{counts.LEARNING > 9 ? '9+' : counts.LEARNING}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {isAdmin && (
          <div className="sidebar-group">
            {!collapsed && <span className="sidebar-group-label">ADMIN</span>}
            <ul className="sidebar-list">
              <li>
                <NavLink
                  to="/admin"
                  className={({ isActive }) => `sidebar-item ${isActive ? 'sidebar-item--active' : ''}`}
                  title={collapsed ? 'Admin' : undefined}
                  onClick={onMobileClose}
                >
                  <span className="sidebar-item-icon"><AdminIcon /></span>
                  {!collapsed && <span className="sidebar-item-label">Admin Panel</span>}
                </NavLink>
              </li>
            </ul>
          </div>
        )}
      </nav>

      {/* Admin user analytics card */}
      {isAuthenticated && user && isAdmin && (
        <AdminUsersCard collapsed={collapsed} />
      )}

      {/* User card */}
      {isAuthenticated && user && (
        <UserCard user={user} collapsed={collapsed} navigate={navigate} />
      )}
    </aside>
  );
}

export default Sidebar;

/* ── Admin users analytics card ─────────────────────── */
function AdminUsersCard({ collapsed }: { collapsed: boolean }) {
  const [open, setOpen]   = useState(false);
  const [popPos, setPopPos] = useState<{ bottom: number; left: number; width: number } | null>(null);
  const [stats, setStats] = useState<CommunityUserStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const ref     = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    if (stats) return;
    setLoading(true);
    authApi.adminGetUserStats()
      .then((r) => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (!open && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setPopPos({ bottom: window.innerHeight - rect.top + 8, left: rect.left, width: Math.max(rect.width, 220) });
    }
    setOpen((o) => !o);
  };

  return (
    <div className="sb-user-wrap" ref={ref}>
      {open && popPos && (
        <div className="sb-popover" style={{ position: 'fixed', bottom: popPos.bottom, left: popPos.left, width: popPos.width, zIndex: 300 }}>
          <div className="sb-pop-header">
            <div className="sb-pop-avatar sb-pop-avatar--admin"><UsersIcon /></div>
            <div className="sb-pop-info">
              <span className="sb-pop-name">Community Users</span>
              <span className="sb-pop-email">Analytics overview</span>
            </div>
          </div>
          <div className="sb-pop-divider" />
          {loading ? (
            <div className="sb-pop-loading">Loading…</div>
          ) : stats ? (
            <>
              <div className="sb-pop-row">
                <span className="sb-pop-label">Total Users</span>
                <span className="sb-pop-val">{stats.totalUsers.toLocaleString()}</span>
              </div>
              <div className="sb-pop-row">
                <span className="sb-pop-label">Premium</span>
                <span className="sb-pop-val" style={{ color: '#d97706' }}>⚡ {stats.premiumUsers.toLocaleString()}</span>
              </div>
              <div className="sb-pop-row">
                <span className="sb-pop-label">Free</span>
                <span className="sb-pop-val">{stats.freeUsers.toLocaleString()}</span>
              </div>
              <div className="sb-pop-divider" />
              <div className="sb-pop-row">
                <span className="sb-pop-label">New this week</span>
                <span className="sb-pop-val sb-pop-val--green">+{stats.newThisWeek}</span>
              </div>
              <div className="sb-pop-row">
                <span className="sb-pop-label">New this month</span>
                <span className="sb-pop-val sb-pop-val--green">+{stats.newThisMonth}</span>
              </div>
              {stats.totalRevenue != null && (
                <>
                  <div className="sb-pop-divider" />
                  <div className="sb-pop-row">
                    <span className="sb-pop-label">Revenue</span>
                    <span className="sb-pop-val sb-pop-val--green">
                      ₹{Number(stats.totalRevenue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="sb-pop-loading">No data</div>
          )}
        </div>
      )}

      <button ref={cardRef} className={`sb-user-card sb-admin-users-card ${collapsed ? 'sb-user-card--collapsed' : ''}`} onClick={handleOpen}>
        <div className="sb-user-avatar sb-admin-users-avatar"><UsersIcon /></div>
        {!collapsed && (
          <div className="sb-user-meta">
            <span className="sb-user-name">Community Users</span>
            <span className="sb-user-status">Analytics</span>
          </div>
        )}
      </button>
    </div>
  );
}

/* ── User card ──────────────────────────────────────── */
function UserCard({ user, collapsed, navigate }: { user: any; collapsed: boolean; navigate: (p: any) => void }) {
  const [open, setOpen]           = useState(false);
  const [popPos, setPopPos]       = useState<{ bottom: number; left: number; width: number } | null>(null);
  const [profile, setProfile]     = useState<UserProfile | null>(null);
  const [sub, setSub]             = useState<SubscriptionResponse | null>(null);
  const [loading, setLoading]     = useState(false);
  const ref     = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    if (profile) return;
    setLoading(true);
    Promise.all([authApi.getUserProfile(), authApi.getSubscription()])
      .then(([p, s]) => { setProfile(p.data); setSub(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (!open && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setPopPos({ bottom: window.innerHeight - rect.top + 8, left: rect.left, width: Math.max(rect.width, 220) });
    }
    setOpen((o) => !o);
  };

  const initials = (user.name || user.email || '?').charAt(0).toUpperCase();
  const joinedDate = profile?.createdDate ? fmt(profile.createdDate) : null;

  return (
    <div className="sb-user-wrap" ref={ref}>
      {/* Popover — fixed so it escapes sidebar overflow:hidden */}
      {open && popPos && (
        <div className="sb-popover" style={{ position: 'fixed', bottom: popPos.bottom, left: popPos.left, width: popPos.width, zIndex: 300 }}>
          {loading ? (
            <div className="sb-pop-loading">Loading…</div>
          ) : (
            <>
              {/* Header */}
              <div className="sb-pop-header">
                <div className="sb-pop-avatar">{initials}</div>
                <div className="sb-pop-info">
                  <span className="sb-pop-name">{user.name || 'User'}</span>
                  {user.email && <span className="sb-pop-email">{user.email}</span>}
                </div>
              </div>

              <div className="sb-pop-divider" />

              {/* Joined */}
              {joinedDate && (
                <div className="sb-pop-row">
                  <span className="sb-pop-label">Joined</span>
                  <span className="sb-pop-val">{joinedDate}</span>
                </div>
              )}

              {/* Status */}
              <div className="sb-pop-row">
                <span className="sb-pop-label">Status</span>
                <span className={`sb-pop-badge ${user.isPremium ? 'sb-pop-badge--pro' : 'sb-pop-badge--free'}`}>
                  {user.isPremium ? '⚡ Premium' : 'Free'}
                </span>
              </div>

              {/* Subscription details */}
              {sub && sub.isPremium && (
                <>
                  {sub.planName && (
                    <div className="sb-pop-row">
                      <span className="sb-pop-label">Plan</span>
                      <span className="sb-pop-val">{sub.planName}</span>
                    </div>
                  )}
                  {sub.planPrice != null && (
                    <div className="sb-pop-row">
                      <span className="sb-pop-label">Paid</span>
                      <span className="sb-pop-val">₹{sub.planPrice}</span>
                    </div>
                  )}
                  {sub.startDate && (
                    <div className="sb-pop-row">
                      <span className="sb-pop-label">Started</span>
                      <span className="sb-pop-val">{fmt(sub.startDate)}</span>
                    </div>
                  )}
                  {sub.expiryDate && (
                    <div className="sb-pop-row">
                      <span className="sb-pop-label">Expires</span>
                      <span className="sb-pop-val">{fmt(sub.expiryDate)}</span>
                    </div>
                  )}
                  {sub.daysRemaining > 0 && (
                    <div className="sb-pop-row">
                      <span className="sb-pop-label">Remaining</span>
                      <span className="sb-pop-val sb-pop-val--green">{sub.daysRemaining} days</span>
                    </div>
                  )}
                </>
              )}

              {/* Upgrade CTA for free users */}
              {!user.isPremium && (
                <>
                  <div className="sb-pop-divider" />
                  <button className="sb-pop-upgrade" onClick={() => { navigate('/pricing'); setOpen(false); }}>
                    ⚡ Upgrade to Pro ↗
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Card trigger */}
      <button ref={cardRef} className={`sb-user-card ${collapsed ? 'sb-user-card--collapsed' : ''}`} onClick={handleOpen}>
        <div className="sb-user-avatar">{initials}</div>
        {!collapsed && (
          <div className="sb-user-meta">
            <span className="sb-user-name">{user.name || 'User'}</span>
            <span className={`sb-user-status ${user.isPremium ? 'sb-user-status--pro' : ''}`}>
              {user.isPremium ? '⚡ Premium' : 'Free'}
            </span>
          </div>
        )}
      </button>
    </div>
  );
}

function fmt(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
}

/* ── SVG icons ──────────────────────────────────────── */
function BookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}
function ScreenerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}
function SupportIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  );
}
function ForumIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function LeaderboardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}
function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
function AdminIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function CollapseIcon({ collapsed }: { collapsed: boolean }) {
  return collapsed ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
